import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { Spin, Result, Button, Space, Typography } from 'antd';
import { AndroidOutlined, AppleOutlined, LogoutOutlined } from '@ant-design/icons';
import { useAuth } from '../hooks/useAuth';

const { Text, Link } = Typography;

interface ProtectedRouteProps {
  children: React.ReactNode;
}

export const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ children }) => {
  const { isAuthenticated, isLoading, hasAnyRole, isAdmin, logout, user } = useAuth();
  const location = useLocation();

  // Still checking auth
  if (isLoading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <Spin size="large" tip="Checking authentication..." />
      </div>
    );
  }

  // Not authenticated
  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  // Staff-only users should default to Operations page instead of Dashboard
  // Only redirect if on root path (/) and user is STAFF but not ADMIN or OWNER
  if (
    user &&
    location.pathname === '/' &&
    hasAnyRole('STAFF') &&
    !isAdmin() &&
    !hasAnyRole('OWNER')
  ) {
    return <Navigate to="/operations" replace />;
  }

  // Authenticated but no membership roles
  // Wait until we have user data loaded before checking roles
  if (user && !hasAnyRole('ADMIN', 'OWNER', 'STAFF')) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '100vh',
        padding: 20,
        background: 'linear-gradient(135deg, #003874 0%, #94AFCC 100%)'
      }}>
        <Result
          status="info"
          title="Access Restricted"
          subTitle={
            <Space direction="vertical" size="middle">
              <Text>
                Your account does not have admin, owner, or staff access.
              </Text>
              <div>
                <Text strong>To use the ShadesParking app:</Text>
                <br />
                <Space style={{ marginTop: 8 }}>
                  <Link href="https://play.google.com/store/apps/details?id=com.shadesparking" target="_blank">
                    <Button icon={<AndroidOutlined />}>Google Play</Button>
                  </Link>
                  <Link href="https://apps.apple.com/app/shadesparking/id123456789" target="_blank">
                    <Button icon={<AppleOutlined />}>App Store</Button>
                  </Link>
                </Space>
              </div>
              <Text type="secondary">
                If you're a staff member or manager, please contact your organization administrator to request access.
              </Text>
            </Space>
          }
          extra={
            <Button
              type="primary"
              icon={<LogoutOutlined />}
              onClick={async () => {
                await logout();
                window.location.href = '/login';
              }}
            >
              Logout
            </Button>
          }
          style={{
            background: 'white',
            borderRadius: 8,
            padding: 40,
            maxWidth: 600,
          }}
        />
      </div>
    );
  }

  return <>{children}</>;
};
